import RenderOnViewportEntry from '@creator/ui/components/RenderOnViewportEntry/RenderOnViewportEntry';
import Spinner from '@creator/ui/components/Spinner/Spinner';
import { ReactLazyPreload } from '@creator/ui/utils/react';
import AdvertisingSlot from '@src/components/Ads/AdvertisingSlot';
import PostView from '@src/components/Post/PostView/PostView';
import TokenSidebar from '@src/components/Token/TokenSidebar/TokenSidebar';
import usePageTitle from '@src/hooks/usePageTitle';
import { useStoreActions } from '@src/model/hooks';
import { isResponsePost } from '@src/model/upvote/helpers';
import { getUserId } from '@src/model/user/helpers';
import { usePost } from '@src/store/posts/hooks/usePost';
import { usePostLoader } from '@src/store/posts/hooks/usePostLoader';
import { postView } from '@src/store/posts/view';
import { isMobile } from '@src/utils/utils';
import { type FC, Fragment, memo, useCallback, useEffect, useLayoutEffect, useState } from 'react';
import BottomStickyAd from '../BottomStickyAd';
import StoryPageHeader from './PostTabHero';

const PartnerList = ReactLazyPreload(() => import('@src/components/Token/PartnerList/PartnerList'));
const TopicsBar = ReactLazyPreload(() => import('../PostsTab/TopicsBar'));
const PartnerPosts = ReactLazyPreload(() => import('../PartnerPosts'));
const CommunitySection = ReactLazyPreload(() => import('../PostsTab/CommunitySection'));

export interface PostTabProps {
    tokenName: string;
    identifier: string;
    commentId?: string;
    replyId?: string;
    className?: string;
}

const PostTab: FC<PostTabProps> = props => {
    const { tokenName, identifier = '', commentId = '', replyId = '', className = '' } = props;

    const loadResponse = useStoreActions(actions => actions.response.loadResponse);
    const { load, loadStatus, postId } = usePostLoader({ identifier });
    const [isPostViewExpanded, setIsPostViewExpanded] = useState(false);

    const post = usePost(postId);
    usePageTitle(getPageTitle());

    function getPageTitle() {
        if (!post) return '';
        if (post.kind === 'story') return post.title;
        return `${post.title} - Community Post`;
    }

    useLayoutEffect(() => {
        CommunitySection.preload();
        TopicsBar.preload();
        PartnerPosts.preload();
        PartnerList.preload();
    }, [commentId]);

    useEffect(() => {
        setIsPostViewExpanded(false);
    }, [postId]);

    const onPostViewExpend = useCallback(() => {
        setIsPostViewExpanded(true);
    }, [postId]);

    useEffect(() => {
        if (!postId) return;
        postView({ postId, tokenName, userId: getUserId() });
        console.log(`[PostTab] postId: ${postId}`);
    }, [postId]);

    useLayoutEffect(() => {
        if (!identifier) return;
        loadContent();
    }, [identifier]);

    async function loadContent() {
        const post = await load();
        if (!post || !isResponsePost(post)) return;
        loadResponse(post.id);
    }

    function renderPost() {
        if (!postId) return null;
        return <PostView onExpand={onPostViewExpend} highlightCommentId={commentId} loadReplyId={replyId} tokenName={tokenName} postId={postId} />;
    }

    function renderSpinner() {
        if (loadStatus !== 'loading' || post) return null;
        return <Spinner className="mx-auto mt-8" />;
    }

    function renderTokenSidebar() {
        if (isMobile()) return;
        return <TokenSidebar postId={postId} showRecommendedPosts={isPostViewExpanded} tokenName={tokenName} className="hidden lg:block lg:w-4/12" />;
    }

    function renderHeader() {
        if (!post) return null;
        if (post.kind === 'story') return <StoryPageHeader postId={postId} tokenName={tokenName} />;
    }

    function renderMoreContent() {
        if (!post) return null;
        if (post.kind === 'story') {
            return (
                <Fragment>
                    <RenderOnViewportEntry>
                        <PartnerList className="border-gray-200 dark:border-gray-600 border-y" tokenName={tokenName} />
                        <TopicsBar className="" tokenName={tokenName} />
                    </RenderOnViewportEntry>
                    <RenderOnViewportEntry>
                        <PartnerPosts excludePostId={postId} className="" tokenName={tokenName} />
                    </RenderOnViewportEntry>
                    <BottomStickyAd postId={postId} tokenName={tokenName} />
                </Fragment>
            );
        }

        return (
            <RenderOnViewportEntry className="bg-[#F6F7FF] dark:bg-gray-800">
                <CommunitySection hideSeparator={true} tokenName={tokenName} />
                <BottomStickyAd postId={postId} tokenName={tokenName} />
            </RenderOnViewportEntry>
        );
    }

    return (
        <div className="mx-auto">
            {renderHeader()}
            <AdvertisingSlot key={postId} className="my-4" location="top_center" tokenName={tokenName} />
            <div className="flex border-t border-gray-200 dark:border-gray-600">
                <div className="w-full lg:w-8/12">
                    {renderPost()}
                    {renderSpinner()}
                </div>
                {renderTokenSidebar()}
            </div>
            {renderMoreContent()}
        </div>
    );
};

export default memo(PostTab);
